import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, X, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Compact, reusable "Request a callback" CTA + minimal modal.
 *
 * Props:
 *   variant   — "inline" (default, text link) | "banner" (rounded chip strip)
 *   context   — optional string passed as service_type so admin knows the entry point
 *   className — wrapper className override
 */
export default function RequestCallbackButton({ variant = "inline", context = "", className = "" }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", notes: "" });
  const firstFieldRef = useRef(null);

  useEffect(() => {
    if (open) {
      setDone(false);
      const t = setTimeout(() => {
        try { firstFieldRef.current?.focus(); } catch (_) {}
      }, 150);
      return () => clearTimeout(t);
    }
  }, [open]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const submit = async (ev) => {
    ev?.preventDefault?.();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and phone number");
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/public/support-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          phone: form.phone.trim(),
          email: form.email.trim() || undefined,
          notes: form.notes.trim() || undefined,
          service_type: context || "callback_request",
        }),
      });
      if (!r.ok) {
        let detail = "Couldn't submit — please try again.";
        try { detail = (await r.json())?.detail || detail; } catch (_) {}
        throw new Error(detail);
      }
      setDone(true);
      setForm({ name: "", phone: "", email: "", notes: "" });
      // Auto-close after a moment
      setTimeout(() => setOpen(false), 2200);
    } catch (e) {
      toast.error(e?.message || "Couldn't submit — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const Trigger = (
    variant === "banner" ? (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-white border border-[#87CEEB]/40 text-[#5BA3C0] hover:bg-[#87CEEB]/10 hover:border-[#87CEEB] transition-colors text-sm font-semibold ${className}`}
        data-testid="request-callback-trigger"
      >
        <Phone className="w-3.5 h-3.5" />
        Request a callback
      </button>
    ) : (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-[#5BA3C0] transition-colors ${className}`}
        data-testid="request-callback-trigger"
      >
        <Phone className="w-3.5 h-3.5 text-[#87CEEB]" />
        <span>Prefer a callback? <span className="text-[#5BA3C0] font-semibold underline-offset-2 hover:underline">Talk to an advisor</span></span>
      </button>
    )
  );

  return (
    <>
      {Trigger}

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] bg-slate-900/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-6"
            onClick={() => !submitting && setOpen(false)}
            data-testid="request-callback-modal"
          >
            <motion.div
              initial={{ y: 24, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 24, opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.18 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-start justify-between px-5 py-4 border-b border-slate-100">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-lg bg-[#87CEEB]/15 flex items-center justify-center">
                    <Phone className="w-4 h-4 text-[#87CEEB]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 text-base leading-tight">Request a callback</h3>
                    <p className="text-[12px] text-slate-500">A support advisor will call within 1 business day.</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => !submitting && setOpen(false)}
                  className="text-slate-400 hover:text-slate-700 p-1 -mr-1"
                  aria-label="Close"
                  data-testid="request-callback-close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Body */}
              {done ? (
                <div className="px-5 py-7 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-emerald-50 flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                  </div>
                  <p className="font-semibold text-slate-900">Thanks — we've got your request.</p>
                  <p className="text-sm text-slate-500 mt-1">An advisor will be in touch shortly.</p>
                </div>
              ) : (
                <form onSubmit={submit} className="p-5 space-y-3" data-testid="request-callback-form">
                  <Input
                    ref={firstFieldRef}
                    type="text"
                    placeholder="Your name"
                    autoComplete="name"
                    className="h-11 rounded-xl text-[15px]"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="request-callback-name"
                  />
                  <Input
                    type="tel"
                    placeholder="Phone number"
                    autoComplete="tel"
                    inputMode="tel"
                    className="h-11 rounded-xl text-[15px]"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    data-testid="request-callback-phone"
                  />
                  <Input
                    type="email"
                    placeholder="Email (optional)"
                    autoComplete="email"
                    inputMode="email"
                    className="h-11 rounded-xl text-[15px]"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="request-callback-email"
                  />
                  <Textarea
                    placeholder="What kind of support are you looking for? (optional)"
                    className="rounded-xl text-[14px] min-h-[72px]"
                    rows={3}
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    data-testid="request-callback-notes"
                  />

                  <Button
                    type="submit"
                    onClick={submit}
                    disabled={submitting}
                    className="w-full h-11 rounded-xl bg-[#87CEEB] hover:bg-[#6BB8DB] text-white font-semibold"
                    data-testid="request-callback-submit"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Request my callback"}
                  </Button>
                  <p className="text-[11px] text-slate-400 text-center pt-1">
                    We'll only use your details to contact you. No spam.
                  </p>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
